/*!
 * Oasis Land — Klaviyo behavioural tracking helper
 * ------------------------------------------------
 * Single source of truth for site-wide Klaviyo events and profile-property
 * writes. Loaded once per page. See docs/klaviyo-tracking-design.md for the
 * event/property catalogue and design rationale.
 *
 * Public API (exposed on window.OasisTrack):
 *   track(name, properties)        Fire a Klaviyo event with deduplication
 *                                  and a session+page context envelope.
 *   identify(properties)            Update the Klaviyo profile.
 *   setProperty(key, value)         Single-key identify().
 *   ready(callback)                 Run after marketing consent + klaviyo.js.
 *   getSessionId()                  Read the current 30-min session UUID.
 *   getFirstTouch()                  Read the sticky first-touch UTM bundle.
 *
 * Buffering: when called before Klaviyo loads OR before marketing consent,
 * track() and identify() go into an in-memory queue (capped at 50). The
 * queue drains on the FIRST tick after BOTH (consent + klaviyo.js) are
 * satisfied. If neither happens within 30 minutes, the queue is dropped
 * silently — marketing data without consent is unusable.
 *
 * Storage: localStorage only for cross-session counters / idempotency
 * tokens, never PII. Keys:
 *   oasis_track_session            Current session UUID + last-seen ms.
 *   oasis_track_first_touch        First-touch UTM bundle (utm_*, fbclid).
 *   oasis_track_fired              Dedup set per session (Set encoded as
 *                                  comma-separated keys). Capped at 200.
 *
 * Consent: integrates with window.OasisConsent. No event is sent to
 * Klaviyo unless OasisConsent.allowed('marketing') is true at send time.
 *
 * Page-context input: the liquid snippet `snippets/oasis-events.liquid`
 * sets window.OasisTrackContext before this file boots; we read it once.
 */

(function () {
  'use strict';

  if (window.OasisTrack && window.OasisTrack.__loaded) return;

  // ---------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------
  var SESSION_IDLE_MS = 30 * 60 * 1000;            // 30 minutes
  var BUFFER_MAX = 50;                              // events held pre-consent
  var BUFFER_DROP_MS = 30 * 60 * 1000;             // 30 minutes
  var DEDUP_SET_MAX = 200;                          // session-scoped fired set
  var SCROLL_THRESHOLDS = [25, 50, 75, 100];
  var VIDEO_THRESHOLDS = [25, 50, 75, 100];
  var ARTICLE_READ_MIN_SECONDS = 30;
  var ARTICLE_READ_MIN_SCROLL = 75;
  var CALCULATOR_DEBOUNCE_MS = 800;
  var STORAGE_KEYS = {
    session: 'oasis_track_session',
    firstTouch: 'oasis_track_first_touch',
    fired: 'oasis_track_fired'
  };

  // ---------------------------------------------------------------------
  // YouTube IFrame API console-noise filter
  // ---------------------------------------------------------------------
  // YouTube's www-widgetapi.js fires postMessage calls back to the parent
  // window with targetOrigin='https://www.youtube.com', but the parent's
  // actual origin is whatever your site is (oasis-land.com). The browser
  // refuses the call and logs an error every ~250ms per embed. The errors
  // are harmless — they're inside YouTube's own code, can't be try/catched
  // from outside the iframe — but a Knowledge Hub page with 19 embeds can
  // pile up hundreds in a minute. We monkey-patch console.error /
  // console.warn to filter that specific message out, leaving every other
  // log intact. Idempotent (won't double-patch if the helper hot-reloads).
  if (!window.__oasisYTFilterInstalled) {
    window.__oasisYTFilterInstalled = true;
    var YT_NOISE_MARKER = "Failed to execute 'postMessage' on 'DOMWindow'";
    function isYTPostMessageNoise(args) {
      for (var i = 0; i < args.length; i++) {
        var a = args[i];
        var s = (typeof a === 'string') ? a : (a && a.message) ? a.message : '';
        if (typeof s === 'string' && s.indexOf(YT_NOISE_MARKER) > -1) return true;
      }
      return false;
    }
    ['error', 'warn'].forEach(function (level) {
      var original = console[level];
      if (typeof original !== 'function') return;
      console[level] = function () {
        if (isYTPostMessageNoise(arguments)) return; // swallow
        return original.apply(console, arguments);
      };
    });
    // Belt-and-braces: also suppress uncaught-error surfacing for the same
    // text. Some Chrome versions route postMessage origin failures through
    // window 'error' events rather than console.error.
    window.addEventListener('error', function (e) {
      if (e && e.message && e.message.indexOf(YT_NOISE_MARKER) > -1) {
        e.preventDefault();
        return true;
      }
    }, true);
  }

  // ---------------------------------------------------------------------
  // Tiny utilities (no jQuery, no polyfills beyond evergreen browsers)
  // ---------------------------------------------------------------------
  var ctx = window.OasisTrackContext || {};
  var debug = !!(window.OasisDebug || (typeof URLSearchParams !== 'undefined' && new URLSearchParams(location.search).has('oasis_debug')));
  function log() {
    if (!debug) return;
    try { console.log.apply(console, ['[OasisTrack]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function readLocal(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function writeLocal(key, value) {
    try { localStorage.setItem(key, value); } catch (e) {}
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'oxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function now() { return Date.now(); }
  function nowIso() { return new Date().toISOString(); }

  function throttle(fn, wait) {
    var last = 0;
    var raf = 0;
    return function () {
      var args = arguments;
      var t = now();
      if (t - last >= wait) {
        last = t;
        fn.apply(null, args);
      } else if (!raf) {
        raf = requestAnimationFrame(function () {
          raf = 0;
          last = now();
          fn.apply(null, args);
        });
      }
    };
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  function isObject(v) { return v && typeof v === 'object' && !Array.isArray(v); }

  // ---------------------------------------------------------------------
  // Session management — 30-minute idle window, UUID kept in localStorage
  // ---------------------------------------------------------------------
  function readSession() {
    var raw = readLocal(STORAGE_KEYS.session);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.id || !parsed.lastSeen) return null;
      if (now() - parsed.lastSeen > SESSION_IDLE_MS) return null;
      return parsed;
    } catch (e) { return null; }
  }
  function writeSession(sess) {
    writeLocal(STORAGE_KEYS.session, JSON.stringify({ id: sess.id, lastSeen: now() }));
  }
  function getSession() {
    var s = readSession();
    if (!s) {
      s = { id: uuid(), lastSeen: now() };
      writeLocal(STORAGE_KEYS.session, JSON.stringify(s));
    } else {
      writeSession(s);
    }
    return s;
  }
  var session = getSession();
  // Refresh the last-seen on every interaction so the window slides.
  ['pointerdown', 'keydown', 'scroll', 'visibilitychange'].forEach(function (evt) {
    window.addEventListener(evt, function () { writeSession(session); }, { passive: true });
  });

  // ---------------------------------------------------------------------
  // First-touch UTM capture — sticky for the life of the browser profile.
  // ---------------------------------------------------------------------
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];
  function captureFirstTouch() {
    if (readLocal(STORAGE_KEYS.firstTouch)) return;
    try {
      var sp = new URLSearchParams(location.search);
      var bundle = {};
      var seen = false;
      UTM_KEYS.forEach(function (k) {
        var v = sp.get(k);
        if (v) { bundle[k] = v; seen = true; }
      });
      if (!seen) return;
      bundle.captured_at = nowIso();
      bundle.captured_path = location.pathname;
      writeLocal(STORAGE_KEYS.firstTouch, JSON.stringify(bundle));
    } catch (e) {}
  }
  captureFirstTouch();

  function getFirstTouch() {
    var raw = readLocal(STORAGE_KEYS.firstTouch);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  // ---------------------------------------------------------------------
  // Per-session dedup set
  // ---------------------------------------------------------------------
  function readFiredSet() {
    var raw = sessionStorage.getItem(STORAGE_KEYS.fired);
    if (!raw) return {};
    try { return JSON.parse(raw) || {}; } catch (e) { return {}; }
  }
  function writeFiredSet(set) {
    try { sessionStorage.setItem(STORAGE_KEYS.fired, JSON.stringify(set)); } catch (e) {}
  }
  function markFired(key) {
    var set = readFiredSet();
    var keys = Object.keys(set);
    if (keys.length >= DEDUP_SET_MAX) {
      // FIFO eviction
      delete set[keys[0]];
    }
    set[key] = 1;
    writeFiredSet(set);
  }
  function hasFired(key) {
    return !!readFiredSet()[key];
  }

  // ---------------------------------------------------------------------
  // Consent + Klaviyo readiness
  // ---------------------------------------------------------------------
  function marketingAllowed() {
    return !!(window.OasisConsent && window.OasisConsent.allowed && window.OasisConsent.allowed('marketing'));
  }

  function klaviyoReady() {
    // Onsite JS exposes the global `klaviyo` (and the legacy `_learnq` queue).
    return !!(window._learnq || window.klaviyo);
  }

  // ---------------------------------------------------------------------
  // Suppress Klaviyo's auto-fired "Viewed Product".
  // The Klaviyo onsite JS auto-detects Shopify product pages and pushes
  // `['track', 'Viewed Product', {...plain payload...}]` to _learnq. We
  // fire our own enriched `Viewed Lot` event instead, so the default
  // Viewed Product is redundant noise. We monkey-patch _learnq.push to
  // intercept and drop those default Viewed Product entries. Our own
  // events (which use the metric names "Viewed Lot", "Quiz Step
  // Completed", etc.) pass through untouched.
  // ---------------------------------------------------------------------
  function isDefaultViewedProductCall(args) {
    if (!args) return false;
    var arr = args;
    if (arguments.length > 1) arr = Array.prototype.slice.call(arguments);
    if (!Array.isArray(arr) || arr.length < 2) return false;
    if (arr[0] !== 'track') return false;
    if (arr[1] !== 'Viewed Product') return false;
    // Klaviyo's default payload uses keys like ProductID / Name / Brand
    // (PascalCase). Our helper never fires Viewed Product. So any push
    // with name === 'Viewed Product' that we see is from Klaviyo's
    // onsite JS and should be dropped.
    return true;
  }

  function installLearnqFilter() {
    if (!window._learnq) window._learnq = [];
    var q = window._learnq;
    if (q.__oasisFiltered) return;
    q.__oasisFiltered = true;
    var origPush = q.push;
    q.push = function () {
      // Klaviyo calls push with a single array arg most of the time.
      var first = arguments[0];
      if (isDefaultViewedProductCall(first)) {
        log('suppressed default Viewed Product');
        return q.length; // pretend it pushed, return new length
      }
      // Also handle the multi-arg form just in case.
      if (arguments.length > 1) {
        var collected = Array.prototype.slice.call(arguments);
        if (isDefaultViewedProductCall(collected)) {
          log('suppressed default Viewed Product (multi-arg form)');
          return q.length;
        }
      }
      return origPush.apply(q, arguments);
    };
  }

  // Install at boot, and re-install on a timer for ~15s in case
  // klaviyo.js replaces the _learnq object after it loads.
  installLearnqFilter();
  var filterAttempts = 0;
  var filterPoll = setInterval(function () {
    filterAttempts++;
    installLearnqFilter();
    if (filterAttempts > 30) clearInterval(filterPoll);
  }, 500);

  // ---------------------------------------------------------------------
  // Buffering + drain
  // ---------------------------------------------------------------------
  var buffer = [];
  var bufferStartedAt = now();
  function pushBuffer(entry) {
    if (now() - bufferStartedAt > BUFFER_DROP_MS) {
      buffer.length = 0;
      bufferStartedAt = now();
    }
    if (buffer.length >= BUFFER_MAX) buffer.shift();
    buffer.push(entry);
  }

  function drainOnce() {
    if (!marketingAllowed() || !klaviyoReady()) return;
    if (!buffer.length) return;
    log('Draining buffer with', buffer.length, 'entries');
    var pending = buffer.splice(0);
    pending.forEach(function (entry) {
      if (entry.type === 'track') {
        _sendTrack(entry.name, entry.props);
      } else if (entry.type === 'identify') {
        _sendIdentify(entry.props);
      }
    });
  }

  // ---------------------------------------------------------------------
  // Direct send (private)
  // ---------------------------------------------------------------------
  function _sendTrack(name, props) {
    try {
      window._learnq = window._learnq || [];
      var enriched = enrichProperties(props || {});
      window._learnq.push(['track', name, enriched]);
      log('track →', name, enriched);
    } catch (e) { log('track failed', name, e); }
  }
  function _sendIdentify(props) {
    try {
      if (!isObject(props)) return;
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', props]);
      log('identify →', props);
    } catch (e) { log('identify failed', e); }
  }

  function enrichProperties(props) {
    var enriched = {};
    var k;
    for (k in props) if (props.hasOwnProperty(k)) enriched[k] = props[k];

    enriched.session_id = session.id;
    enriched.page_type = ctx.pageType || (document.body && document.body.dataset ? document.body.dataset.pageType : '');
    enriched.path = location.pathname;
    enriched.locale = ctx.locale || (document.documentElement && document.documentElement.lang) || '';
    enriched.market_handle = ctx.marketHandle || '';
    enriched.event_source_url = location.href;

    var ft = getFirstTouch();
    if (ft) {
      if (ft.utm_source) enriched.first_touch_utm_source = ft.utm_source;
      if (ft.utm_medium) enriched.first_touch_utm_medium = ft.utm_medium;
      if (ft.utm_campaign) enriched.first_touch_utm_campaign = ft.utm_campaign;
    }
    return enriched;
  }

  // ---------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------
  var readyCallbacks = [];
  function fireReady() {
    while (readyCallbacks.length) {
      try { readyCallbacks.shift()(); } catch (e) { log('ready cb failed', e); }
    }
  }

  var api = {
    __loaded: true,
    track: function (name, props) {
      if (!name || typeof name !== 'string') return;
      var dedupKey = props && props.__unique_id ? name + '|' + props.__unique_id : null;
      var clean = props ? Object.assign({}, props) : {};
      delete clean.__unique_id;
      if (dedupKey && hasFired(dedupKey)) {
        log('skip (deduped)', name, dedupKey);
        return;
      }
      if (dedupKey) markFired(dedupKey);
      if (marketingAllowed() && klaviyoReady()) {
        _sendTrack(name, clean);
      } else {
        pushBuffer({ type: 'track', name: name, props: clean });
        log('buffered', name);
      }
    },
    identify: function (props) {
      if (!isObject(props)) return;
      if (marketingAllowed() && klaviyoReady()) {
        _sendIdentify(props);
      } else {
        pushBuffer({ type: 'identify', props: props });
      }
    },
    setProperty: function (key, value) {
      if (!key) return;
      var p = {}; p[key] = value;
      api.identify(p);
    },
    ready: function (cb) {
      if (typeof cb !== 'function') return;
      if (marketingAllowed() && klaviyoReady()) {
        try { cb(); } catch (e) { log('ready cb failed', e); }
      } else {
        readyCallbacks.push(cb);
      }
    },
    getSessionId: function () { return session.id; },
    getFirstTouch: getFirstTouch
  };
  window.OasisTrack = api;

  // ---------------------------------------------------------------------
  // Watch for consent + klaviyo readiness, drain when both happen
  // ---------------------------------------------------------------------
  function tryReady() {
    if (marketingAllowed() && klaviyoReady()) {
      drainOnce();
      fireReady();
      return true;
    }
    return false;
  }

  window.addEventListener('oasis:consent-granted', tryReady);
  window.addEventListener('oasis:consent-updated', tryReady);

  // Poll briefly for klaviyo.js (it loads async via the marketing-consent
  // loader). We back off the poll quickly to avoid background CPU.
  (function pollKlaviyo() {
    var attempts = 0;
    var poll = function () {
      attempts++;
      if (tryReady()) return;
      if (attempts < 60) setTimeout(poll, 250); // up to ~15s
    };
    setTimeout(poll, 250);
  })();

  // ---------------------------------------------------------------------
  // Customer identify — if Shopify customer is logged in, ctx.customer is
  // present. Fire identify with email so subsequent events attach.
  // ---------------------------------------------------------------------
  if (ctx.customer && ctx.customer.email) {
    api.ready(function () {
      api.identify({
        $email: ctx.customer.email,
        $first_name: ctx.customer.first_name || undefined,
        $last_name: ctx.customer.last_name || undefined,
        $id: ctx.customer.id || undefined
      });
    });
  }

  // ---------------------------------------------------------------------
  // EVENT WIRES — each in its own function so it can be added/removed
  // ---------------------------------------------------------------------

  // ----- Viewed Lot (product pages) -----
  function wireViewedLot() {
    if (ctx.pageType !== 'product' || !ctx.product || !ctx.product.handle) return;
    var p = ctx.product;
    // Fire after 3s on idle to avoid bouncers and to ensure consent has had
    // a chance to be granted.
    var fire = function () {
      api.track('Viewed Lot', {
        __unique_id: p.handle + ':' + session.id,
        lot_handle: p.handle,
        lot_id: p.id,
        lot_name: p.title,
        lot_url: p.url,
        lot_price: p.price,
        lot_size: p.metafields && p.metafields.lot_size,
        lot_location: (p.metafields && p.metafields.lot_location) || (p.metafields && p.metafields.location),
        lot_tier: p.metafields && p.metafields.lot_tier,
        development: p.metafields && p.metafields.development,
        island: p.metafields && p.metafields.island,
        view_type: p.metafields && p.metafields.view_type
      });

      // Sticky profile properties for personalisation tokens in emails.
      api.identify({
        'Last Lot Viewed Handle': p.handle,
        'Last Lot Viewed URL': p.url,
        'Last Lot Viewed Name': p.title,
        'Last Lot Viewed Price': p.price,
        'Last Lot Viewed Island': p.metafields && p.metafields.island,
        'Last Lot Viewed View Type': p.metafields && p.metafields.view_type,
        'Last Lot Viewed Tier': p.metafields && p.metafields.lot_tier,
        'Last Lot Viewed Development': p.metafields && p.metafields.development,
        'Last Lot Viewed Location': (p.metafields && p.metafields.lot_location) || (p.metafields && p.metafields.location),
        'Last Lot Viewed At': nowIso()
      });
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(fire, { timeout: 3500 });
    } else {
      setTimeout(fire, 3000);
    }
  }

  // ----- Viewed Lot Drawer (desktop "View Plot Details" button) -----
  function wireLotDrawer() {
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest('[data-oasis-view-plot]');
      if (!trigger) return;
      var handle = trigger.getAttribute('data-product-handle') || trigger.getAttribute('data-handle') || '';
      var id = trigger.getAttribute('data-product-id') || '';
      var bucket = Math.floor(now() / 60000);
      api.track('Viewed Lot Drawer', {
        __unique_id: (handle || id) + ':' + bucket,
        lot_handle: handle || undefined,
        lot_id: id || undefined,
        lot_url: trigger.getAttribute('data-product-url') || trigger.getAttribute('data-url') || ''
      });
    }, { capture: true, passive: true });
  }

  // ----- Quiz events (subscribe to existing oasis_land:quiz_event) -----
  // The quiz emits exactly three event names we care about:
  //   - 'quiz_start'   : fires once when the welcome-screen CTA is clicked
  //   - 'quiz_step'    : fires per step selection. Payload includes
  //                      step_number (1-4), step_name (goal|island|budget|view),
  //                      and selected_option (the human label).
  //   - 'quiz_complete': fires when results render. Payload includes
  //                      goal, island, budget, view.
  // We ignore quiz_lead_capture and quiz_cta_click here (handled elsewhere
  // or out of scope for scoring).
  function wireQuiz() {
    window.addEventListener('oasis_land:quiz_event', function (e) {
      var d = (e && e.detail) || {};
      var name = d.event || d.quiz_event || '';
      if (!name) return;

      if (name === 'quiz_start') {
        api.track('Quiz Started', {
          __unique_id: session.id + ':quiz-started'
        });
        return;
      }

      if (name === 'quiz_step') {
        var stepNumber = d.step_number;
        var stepName = d.step_name || '';
        var selected = d.selected_option || '';

        api.track('Quiz Step Completed', {
          __unique_id: session.id + ':quiz-step-' + stepNumber,
          step_number: stepNumber,
          step_name: stepName,
          selected_option: selected
        });

        // Sticky profile properties matching the dual-flow interest_tag
        // and island_preference fields specified in the handover.
        var ident = {};
        if (stepName === 'goal' && selected) ident['Quiz Vision'] = selected;
        else if (stepName === 'island' && selected) ident['Quiz Island Preference'] = selected;
        else if (stepName === 'budget' && selected) ident['Quiz Budget Range'] = selected;
        else if (stepName === 'view' && selected) ident['Quiz View Preference'] = selected;
        if (Object.keys(ident).length) api.identify(ident);
        return;
      }

      if (name === 'quiz_complete') {
        api.track('Quiz Completed', {
          __unique_id: session.id + ':quiz-completed',
          goal: d.goal,
          island: d.island,
          budget: d.budget,
          view: d.view,
          matched_lot_handle: d.lot_handle || d.matched_lot_handle || d.matched_lot
        });
        api.identify({
          'Quiz Completed At': nowIso(),
          'Quiz Vision': d.goal || undefined,
          'Quiz Island Preference': d.island || undefined,
          'Quiz Budget Range': d.budget || undefined,
          'Quiz View Preference': d.view || undefined,
          'Quiz Match Lot Handle': d.lot_handle || d.matched_lot_handle || d.matched_lot || undefined
        });
        return;
      }

      // Any other quiz_* event is ignored by this scoring helper.
    });
  }

  // ----- Calculated Payment (budget calculator) -----
  // Two calculator shapes exist on the site:
  //   1) Standalone budget calc (budget-calculator.liquid): uses element
  //      IDs #landPrice / #depositPercentage / #years / #monthlyPaymentResult.
  //   2) Product-page modal calc (product-oasisdrawer-main.liquid): uses
  //      [data-oasis-monthly-display] for the visible amount and
  //      [data-oasis-property="monthly-payment|deposit|term-months|lot-price"]
  //      hidden form inputs that the modal script populates.
  // We read whichever exists and fire Calculated Payment when the value
  // changes via either DOM input events or a MutationObserver on the
  // displayed monthly amount.
  function wireCalculator() {
    var CALC_INPUT_IDS = { landPrice: 1, depositPercentage: 1, years: 1 };

    function readById(id) {
      var el = document.getElementById(id);
      return readVal(el);
    }
    function readVal(el) {
      if (!el) return NaN;
      var raw = el.value !== undefined && el.value !== '' ? el.value : (el.textContent || '');
      var n = parseFloat(String(raw).replace(/[^0-9.\-]/g, ''));
      return isFinite(n) ? n : NaN;
    }
    function readDataProp(name) {
      var el = document.querySelector('[data-oasis-property="' + name + '"]');
      return readVal(el);
    }

    var fire = debounce(function () {
      // Try standalone IDs first, then fall back to data-oasis-property.
      var landPrice = readById('landPrice');
      if (!isFinite(landPrice) || landPrice <= 0) landPrice = readDataProp('lot-price');
      var deposit = readById('depositPercentage');
      if (!isFinite(deposit)) deposit = readDataProp('deposit');
      var years = readById('years');
      if (!isFinite(years) || years <= 0) {
        var termMonths = readDataProp('term-months');
        if (isFinite(termMonths) && termMonths > 0) years = Math.round(termMonths / 12);
      }
      // Monthly: try #monthlyPaymentResult, then the hidden form input,
      // then the visible display element.
      var monthly = readById('monthlyPaymentResult');
      if (!isFinite(monthly) || monthly <= 0) monthly = readDataProp('monthly-payment');
      if (!isFinite(monthly) || monthly <= 0) {
        var disp = document.querySelector('[data-oasis-monthly-display]');
        monthly = readVal(disp);
      }
      if (!isFinite(monthly) || monthly <= 0) return;
      if (!isFinite(landPrice) || landPrice <= 0) {
        // Fall back to the product context if we're on a product page.
        landPrice = (ctx.product && ctx.product.price) || NaN;
      }
      if (!isFinite(landPrice) || landPrice <= 0) return;

      var handle = (ctx.product && ctx.product.handle) || '';
      api.track('Calculated Payment', {
        __unique_id: session.id + ':' + handle + ':' + Math.round(monthly),
        lot_handle: handle || undefined,
        land_price: landPrice,
        deposit_pct: deposit,
        years: years,
        monthly_payment: Math.round(monthly * 100) / 100
      });

      api.identify({
        'Last Calculator Monthly Payment': Math.round(monthly * 100) / 100,
        'Last Calculator Deposit Pct': deposit,
        'Last Calculator Years': years,
        'Last Calculator Land Price': landPrice,
        'Last Calculator At': nowIso()
      });
    }, CALCULATOR_DEBOUNCE_MS);

    function maybeFireFromTarget(target) {
      if (!target) return;
      if (target.id && CALC_INPUT_IDS[target.id]) { fire(); return; }
      // Modal calc inputs: anything declaring its role with data-oasis-property
      // (term-months, deposit, monthly-payment, lot-price, payment-mode).
      if (target.getAttribute && target.getAttribute('data-oasis-property')) { fire(); return; }
      // Also any input inside the payment calc modal.
      if (target.closest && target.closest('#paymentCalcModal, .modal-reservation-form, .calc-modal-content')) {
        fire();
      }
    }

    // Delegated listeners catch any calc, regardless of when it mounts.
    document.addEventListener('input', function (e) { maybeFireFromTarget(e.target); }, true);
    document.addEventListener('change', function (e) { maybeFireFromTarget(e.target); }, true);
    document.addEventListener('click', function (e) {
      // Many modal calcs use clickable option chips (e.g. deposit %, term)
      // that mutate the form values via JS without firing a native input
      // event. Catch any click inside the modal calc area.
      var modalEl = e.target && e.target.closest && e.target.closest('#paymentCalcModal, .calc-modal-content');
      if (modalEl) fire();
    }, true);

    // Watch ALL "monthly payment" display elements for textContent changes
    // — the existing calculator scripts mutate them directly, so this
    // catches calculations done by chips / sliders that don't fire input.
    var observer;
    function attachResultObserver() {
      if (!observer) observer = new MutationObserver(function () { fire(); });
      var targets = [];
      var idEl = document.getElementById('monthlyPaymentResult');
      if (idEl) targets.push(idEl);
      // Modal monthly displays.
      Array.prototype.push.apply(targets, document.querySelectorAll('[data-oasis-monthly-display]'));
      // Hidden form input that gets repopulated by the calc script.
      Array.prototype.push.apply(targets, document.querySelectorAll('[data-oasis-property="monthly-payment"]'));
      targets.forEach(function (el) {
        if (!el || el.__oasisObserved) return;
        el.__oasisObserved = true;
        observer.observe(el, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['value'] });
      });
    }
    attachResultObserver();
    // Re-check periodically for late-mounted calculators (e.g. modal opens).
    var attempts = 0;
    var poll = setInterval(function () {
      attachResultObserver();
      attempts++;
      if (attempts > 40) clearInterval(poll); // ~20s
    }, 500);

    // Started Reservation — delegated submit handler.
    // Catches ANY cart-add form on the site (calculator's Reserve form,
    // product page Reserve, collection card Reserve, drawer Reserve).
    // The lot_handle is resolved from the form's hidden inputs, the
    // closest [data-product-handle] element, or the page context.
    function fireStartedReservation(handle, source) {
      var monthly = readById('monthlyPaymentResult');
      api.track('Started Reservation', {
        __unique_id: session.id + ':' + (handle || 'unknown'),
        lot_handle: handle || (ctx.product && ctx.product.handle) || undefined,
        source: source || undefined,
        monthly_payment: isFinite(monthly) ? Math.round(monthly * 100) / 100 : undefined,
        deposit_pct: readById('depositPercentage'),
        years: readById('years')
      });
    }

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.tagName || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (!/cart\/add/.test(action)) return;

      // Try to resolve the lot handle from a few likely places.
      var handle = '';
      var handleAttr = form.querySelector('[name="properties[handle]"], [data-product-handle]');
      if (handleAttr) handle = handleAttr.value || handleAttr.getAttribute('data-product-handle') || '';
      if (!handle && ctx.product && ctx.product.handle) handle = ctx.product.handle;

      // Source tag: which UI surface did the submit come from?
      var calcEl = form.closest('#calculator, [id^="calculator"], .land-calculator, .payment-calc, .payment-calculator');
      var cardEl = form.closest('[data-product-handle], .product-card, .oasis-property-card, .product-card-land, .product-card-revamp');
      var drawerEl = form.closest('.product-drawer, [id^="product-drawer-"], .slide-over-panel');
      var source = calcEl ? 'calculator'
                 : drawerEl ? 'drawer'
                 : cardEl ? 'card'
                 : (ctx.pageType === 'product' ? 'product-page' : 'unknown');

      fireStartedReservation(handle, source);
    }, true);

    // Also catch direct Reserve button clicks where the button is NOT
    // inside a form (some collection cards open a quick-add or link).
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest(
        '[data-oasis-reserve], button.reserve, a.reserve, .reserve-cta, [data-reserve]'
      );
      if (!btn) return;
      // If the click is inside a form that will also submit, the submit
      // handler above will fire — avoid duplicate firing.
      if (btn.closest('form[action*="cart/add"]')) return;
      var handle = btn.getAttribute('data-product-handle') ||
                   btn.getAttribute('data-handle') ||
                   (btn.closest('[data-product-handle]') && btn.closest('[data-product-handle]').getAttribute('data-product-handle')) ||
                   '';
      fireStartedReservation(handle, 'button-click');
    }, true);
  }

  // ----- Storefront form submitted -----
  // Fires when any user-facing form is submitted on the storefront,
  // EXCLUDING cart-add forms (those become Started Reservation), Klaviyo
  // signup forms (Klaviyo tracks those itself), and Shopify account/auth
  // forms. The intent is to capture lead-gen-style submissions: contact
  // forms, brochure-request forms, quiz lead-capture forms, etc.
  function wireFormSubmitted() {
    var SKIP_ACTION_PATTERNS = [
      /\/cart\/add/i,
      /\/cart\/update/i,
      /\/cart\/change/i,
      /\/cart\.js/i,
      /\/checkout/i,
      /\/account\/login/i,
      /\/account\/register/i,
      /\/account\/recover/i,
      /\/account\/activate/i,
      /\/localization/i,
      /klaviyo\.com/i,
      /\/search/i
    ];
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (form.getAttribute('data-oasis-track') === 'off') return;
      var action = (form.getAttribute('action') || '').replace(/^https?:\/\/[^/]+/i, '').trim();
      for (var i = 0; i < SKIP_ACTION_PATTERNS.length; i++) {
        if (SKIP_ACTION_PATTERNS[i].test(action)) return;
      }
      // Only count if the form has at least one email or phone input
      // (otherwise it's likely a filter, search, or non-lead form).
      var hasContact = form.querySelector('input[type="email"], input[name*="email" i], input[type="tel"], input[name*="phone" i]');
      if (!hasContact) return;

      var formName = form.getAttribute('data-oasis-form-name') ||
                     form.getAttribute('name') ||
                     form.getAttribute('id') ||
                     'unknown';
      var emailInput = form.querySelector('input[type="email"], input[name*="email" i]');
      var emailVal = emailInput ? (emailInput.value || '').trim() : '';

      // If the form carries an email, also identify the profile so the
      // event attaches and future browsing links to this identity.
      if (emailVal && /@/.test(emailVal)) {
        api.identify({ $email: emailVal });
      }

      api.track('Submitted Form', {
        __unique_id: session.id + ':form:' + formName + ':' + Math.floor(now() / 60000),
        form_name: formName,
        action: action,
        page_type: ctx.pageType,
        path: location.pathname
      });
    }, true);
  }

  // ----- Opened Payment Calculator -----
  // Fires when the user clicks the "Click here to customise your
  // interest-free payment plan" banner (or any element wired as a
  // calculator opener). This is distinct from `Calculated Payment`
  // (which fires only when they actually change an input) — opening
  // the widget is itself a high-intent micro-signal.
  function wireOpenedCalculator() {
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest(
        '.price-interactive-container, .js-reserve-open, [aria-label="Open payment calculator"], [aria-label="Open Payment Calculator"], [data-oasis-open-calculator], [onclick*="openPaymentCalculator"]'
      );
      if (!trigger) return;
      var handle = (ctx.product && ctx.product.handle) || '';
      var bucket = Math.floor(now() / 60000);
      api.track('Opened Payment Calculator', {
        __unique_id: (handle || 'unknown') + ':opened-calc:' + bucket,
        lot_handle: handle || undefined,
        lot_price: ctx.product && ctx.product.price,
        page_type: ctx.pageType,
        path: location.pathname
      });
    }, { capture: true, passive: true });
  }

  // ----- Viewed Payment Plan Page -----
  // Fires once per session when the visitor lands on a dedicated payment
  // plan page (matches the handover's "Payment plan page = 10 points"
  // page-visit signal). Detected via URL pattern OR presence of a
  // calculator widget on a non-product page.
  function wireViewedPaymentPlan() {
    var urlMatch = /\/pages\/(payment-plan|payment-plans|aiseo-payment-plans|how-it-works)/i.test(location.pathname);
    var hasCalc = !!document.getElementById('calculator') ||
                  !!document.getElementById('monthlyPaymentResult') ||
                  !!document.querySelector('.land-calculator, .payment-calc');
    if (!urlMatch && !(hasCalc && ctx.pageType !== 'product')) return;
    api.track('Viewed Payment Plan Page', {
      __unique_id: location.pathname + ':' + session.id,
      page_type: ctx.pageType,
      has_calculator: hasCalc
    });
  }

  // ----- Scroll depth -----
  function wireScroll() {
    if (document.body && document.body.classList.contains('shopify-design-mode')) return;
    var docEl = document.documentElement;
    var firedKey = 'scroll:' + location.pathname + ':';
    var thresholds = SCROLL_THRESHOLDS.slice();
    var onScroll = throttle(function () {
      if (!thresholds.length) return;
      var scrollTop = window.pageYOffset || docEl.scrollTop || 0;
      var viewport = window.innerHeight || docEl.clientHeight || 0;
      var height = (docEl.scrollHeight || document.body.scrollHeight || 0) - viewport;
      if (height <= 0) return;
      var pct = Math.min(100, Math.max(0, Math.round((scrollTop / height) * 100)));
      while (thresholds.length && pct >= thresholds[0]) {
        var depth = thresholds.shift();
        if (hasFired(firedKey + depth)) continue;
        markFired(firedKey + depth);
        api.track('Scrolled Page', {
          __unique_id: location.pathname + ':' + depth + ':' + session.id,
          depth: depth
        });
      }
    }, 250);
    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once on mount in case the page is already deep (anchor links).
    setTimeout(onScroll, 500);
  }

  // ----- Article reading -----
  function wireArticleRead() {
    if (ctx.pageType !== 'article' || !ctx.article || !ctx.article.handle) return;
    var docEl = document.documentElement;
    var arrived = now();
    var deepEnough = false;
    var firedKey = 'article_read:' + ctx.article.handle;

    function checkAndFire() {
      if (hasFired(firedKey)) return true;
      if (!deepEnough) return false;
      var elapsed = (now() - arrived) / 1000;
      if (elapsed < ARTICLE_READ_MIN_SECONDS) return false;
      markFired(firedKey);
      api.track('Read Article', {
        __unique_id: ctx.article.handle + ':' + session.id,
        article_handle: ctx.article.handle,
        article_title: ctx.article.title || undefined,
        blog_handle: ctx.article.blog_handle || undefined,
        time_on_page_seconds: Math.round(elapsed)
      });
      return true;
    }

    var onScroll = throttle(function () {
      var scrollTop = window.pageYOffset || docEl.scrollTop || 0;
      var viewport = window.innerHeight || docEl.clientHeight || 0;
      var height = (docEl.scrollHeight || document.body.scrollHeight || 0) - viewport;
      if (height <= 0) return;
      var pct = (scrollTop / height) * 100;
      if (pct >= ARTICLE_READ_MIN_SCROLL) {
        deepEnough = true;
        if (checkAndFire()) {
          window.removeEventListener('scroll', onScroll);
        }
      }
    }, 500);
    window.addEventListener('scroll', onScroll, { passive: true });

    // Also check periodically for the time gate (in case the user scrolls fast).
    var interval = setInterval(function () {
      if (checkAndFire()) clearInterval(interval);
    }, 5000);
    window.addEventListener('beforeunload', function () { clearInterval(interval); });
  }

  // ----- Brochure / PDF downloads -----
  function wireBrochures() {
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      var href = a.getAttribute('href') || '';
      var explicit = a.hasAttribute('data-oasis-brochure');
      var isPdf = /\.pdf(\?|#|$)/i.test(href);
      if (!explicit && !isPdf) return;
      var label = a.getAttribute('data-oasis-brochure') || a.textContent.trim().slice(0, 80) || a.getAttribute('aria-label') || '';
      api.track('Downloaded Brochure', {
        __unique_id: href + ':' + session.id,
        brochure_url: href,
        brochure_label: label
      });
      api.setProperty('Last Brochure Downloaded At', nowIso());
    }, { capture: true, passive: true });
  }

  // ----- YouTube video engagement -----
  // Strategy: find every YouTube embed iframe on the page (whether or not
  // it has enablejsapi=1 in the src). If it lacks the flag, rewrite the
  // src to add it BEFORE attaching the YT.Player. Rewriting forces the
  // iframe to reload, but it happens at boot before the user has
  // interacted, so the cost is invisible. Also re-scan periodically so
  // iframes added by carousels / lightboxes / lazy sections are caught.
  function ensureJsApiInUrl(url) {
    if (!url) return url;
    if (/[?&]enablejsapi=1/.test(url)) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'enablejsapi=1';
  }

  function upgradeIframe(iframe) {
    if (iframe.__oasisUpgraded) return false;
    iframe.__oasisUpgraded = true;
    var srcAttr = iframe.getAttribute('src') || '';
    var dataSrc = iframe.getAttribute('data-src') || '';
    if (srcAttr && srcAttr.indexOf('youtube.com/embed') > -1 && !/[?&]enablejsapi=1/.test(srcAttr)) {
      iframe.setAttribute('src', ensureJsApiInUrl(srcAttr));
      return true; // src changed = iframe will reload
    }
    if (dataSrc && dataSrc.indexOf('youtube.com/embed') > -1 && !/[?&]enablejsapi=1/.test(dataSrc)) {
      iframe.setAttribute('data-src', ensureJsApiInUrl(dataSrc));
    }
    return false;
  }

  // Lazy-load the YouTube iframe API only when we have at least one embed.
  function loadYouTubeApi(cb) {
    if (window.YT && window.YT.Player) return cb();
    window.onYouTubeIframeAPIReady = (function (prev) {
      return function () {
        if (prev) try { prev(); } catch (e) {}
        cb();
      };
    })(window.onYouTubeIframeAPIReady);
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) return;
    var tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;
    document.head.appendChild(tag);
  }

  function attachYouTubePlayer(iframe) {
    if (iframe.__oasisPlayerAttached) return;
    iframe.__oasisPlayerAttached = true;
    var src = iframe.getAttribute('src') || iframe.getAttribute('data-src') || '';
    var m = src.match(/youtube\.com\/embed\/([A-Za-z0-9_-]+)/);
    if (!m) return;
    var videoId = m[1];
    var firedThresholds = {};
    var checkInterval = null;
    try {
      if (!iframe.id) iframe.id = 'oasis-yt-' + videoId + '-' + Math.random().toString(36).slice(2, 7);
      new window.YT.Player(iframe.id, {
        events: {
          onStateChange: function (e) {
            if (e.data === window.YT.PlayerState.PLAYING) {
              var playedKey = 'video_played:' + videoId;
              if (!hasFired(playedKey)) {
                markFired(playedKey);
                api.track('Played Lot Video', {
                  __unique_id: videoId + ':' + session.id,
                  video_id: videoId,
                  page_type: ctx.pageType,
                  path: location.pathname
                });
              }
              if (!checkInterval) {
                checkInterval = setInterval(function () {
                  try {
                    var dur = e.target.getDuration();
                    var cur = e.target.getCurrentTime();
                    if (!dur) return;
                    var pct = Math.round((cur / dur) * 100);
                    VIDEO_THRESHOLDS.forEach(function (t) {
                      if (pct >= t && !firedThresholds[t]) {
                        firedThresholds[t] = 1;
                        api.track('Watched Lot Video', {
                          __unique_id: videoId + ':' + t + ':' + session.id,
                          video_id: videoId,
                          depth: t,
                          page_type: ctx.pageType
                        });
                      }
                    });
                  } catch (err) {}
                }, 1000);
              }
            } else if (e.data === window.YT.PlayerState.ENDED) {
              if (checkInterval) { clearInterval(checkInterval); checkInterval = null; }
              if (!firedThresholds[100]) {
                firedThresholds[100] = 1;
                api.track('Watched Lot Video', {
                  __unique_id: videoId + ':100:' + session.id,
                  video_id: videoId,
                  depth: 100,
                  page_type: ctx.pageType
                });
              }
            }
          }
        }
      });
    } catch (err) { log('yt attach failed', err); }
  }

  function processVideoIframe(iframe) {
    var changed = upgradeIframe(iframe);
    loadYouTubeApi(function () {
      setTimeout(function () { attachYouTubePlayer(iframe); }, changed ? 500 : 0);
    });
  }

  function wireYouTube() {
    function findIframes() {
      return Array.prototype.slice.call(document.querySelectorAll(
        'iframe[src*="youtube.com/embed"], iframe[data-src*="youtube.com/embed"]'
      ));
    }

    findIframes().forEach(processVideoIframe);
    observeForVideos(processVideoIframe);
  }

  // Watch the DOM for new YouTube embed iframes and wire them as they
  // appear. Used both when the page boots empty (no videos yet) and
  // alongside the initial pass so dynamic embeds are caught.
  function observeForVideos(attachFn) {
    if (window.__oasisVideoObserverAttached) return;
    window.__oasisVideoObserverAttached = true;

    function scanNode(node) {
      if (!node || node.nodeType !== 1) return;
      var candidates = [];
      if (node.tagName === 'IFRAME') candidates.push(node);
      else if (node.querySelectorAll) {
        Array.prototype.push.apply(candidates,
          node.querySelectorAll('iframe[src*="youtube.com/embed"], iframe[data-src*="youtube.com/embed"]'));
      }
      candidates.forEach(function (iframe) {
        var changed = upgradeIframe(iframe);
        if (attachFn) {
          // Give the iframe a tick to settle if we just rewrote its src.
          setTimeout(function () { attachFn(iframe); }, changed ? 400 : 0);
        }
      });
    }

    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (!m.addedNodes) return;
        Array.prototype.forEach.call(m.addedNodes, scanNode);
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  // ----- Calendly trigger -----
  function wireCallback() {
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest('[data-open-modal]');
      if (!trigger) return;
      api.track('Opened Callback', {
        __unique_id: session.id + ':opened-callback',
        cta_location: trigger.getAttribute('data-section') || ctx.pageType || location.pathname,
        cta_label: (trigger.textContent || '').trim().slice(0, 80)
      });
    }, { capture: true, passive: true });
  }

  // ----- Knowledge Hub tab views -----
  function wireKnowledgeHub() {
    if (!/\/pages\/knowledge-hub/.test(location.pathname)) return;
    var KNOWN = { '#faqs': 'faqs', '#brochures': 'brochures', '#videos': 'videos', '#blogs': 'blogs' };
    function fire() {
      var tab = KNOWN[location.hash];
      if (!tab) return;
      var key = 'kh_tab:' + tab;
      if (hasFired(key)) return;
      markFired(key);
      api.track('Viewed Knowledge Hub Tab', {
        __unique_id: tab + ':' + session.id,
        tab: tab
      });
    }
    window.addEventListener('hashchange', fire);
    fire();
  }

  // ----- Viewed Page / Viewed Collection -----
  // Generic page-view tracking. Fires once per (path × session). Skips pages
  // where a more specific event already fires (lot, article, knowledge hub,
  // payment plan, quiz). For /collections/* URLs, fires as `Viewed Collection`
  // with the handle as a property — replaces Klaviyo's Shopify auto-detect
  // which doesn't fire reliably on Oasis' custom collection layout.
  //
  // Calls api.track directly (no api.ready wrapper) — api.track itself
  // buffers events when consent/klaviyo aren't ready and drains them on
  // ready, matching every other wire in this file.
  function wireViewedPage() {
    var pageType = ctx.pageType || (document.body && document.body.dataset && document.body.dataset.pageType) || '';
    var path = location.pathname;

    // Skip pages where a more specific event already fires.
    var skipTypes = ['product', 'article'];
    if (skipTypes.indexOf(pageType) > -1) return;
    if (/\/pages\/knowledge-hub/.test(path)) return;
    if (/\/pages\/payment-plan/.test(path) || /\/pages\/finance/.test(path)) return;
    if (/\/pages\/quiz/.test(path) || /\/pages\/lot-finder/.test(path)) return;

    // /collections/* → Viewed Collection (replaces broken Klaviyo Shopify auto-fire)
    // Dedup is handled by api.track via __unique_id; no external hasFired needed.
    if (path.indexOf('/collections/') === 0) {
      var handle = path.replace(/^\/collections\//, '').split('/')[0] || 'all';
      api.track('Viewed Collection', {
        __unique_id: 'collection:' + handle + ':' + session.id,
        collection_handle: handle,
        path: path,
        page_type: 'collection',
        title: (document.title || '').slice(0, 200)
      });
      return;
    }

    // Everything else → Viewed Page
    api.track('Viewed Page', {
      __unique_id: 'page:' + path + ':' + session.id,
      path: path,
      page_type: pageType || 'unknown',
      title: (document.title || '').slice(0, 200)
    });
  }

  // ---------------------------------------------------------------------
  // Boot — wire everything on DOM ready (or now if past it)
  // ---------------------------------------------------------------------
  function boot() {
    try { wireViewedLot(); } catch (e) { log('viewedlot boot fail', e); }
    // wireLotDrawer removed — product drawer UI deprecated.
    try { wireQuiz(); } catch (e) { log('quiz boot fail', e); }
    try { wireCalculator(); } catch (e) { log('calc boot fail', e); }
    try { wireOpenedCalculator(); } catch (e) { log('openedcalc boot fail', e); }
    try { wireFormSubmitted(); } catch (e) { log('form boot fail', e); }
    try { wireViewedPaymentPlan(); } catch (e) { log('payplan boot fail', e); }
    try { wireScroll(); } catch (e) { log('scroll boot fail', e); }
    try { wireArticleRead(); } catch (e) { log('article boot fail', e); }
    try { wireBrochures(); } catch (e) { log('brochure boot fail', e); }
    try { wireYouTube(); } catch (e) { log('youtube boot fail', e); }
    try { wireCallback(); } catch (e) { log('callback boot fail', e); }
    try { wireKnowledgeHub(); } catch (e) { log('kh boot fail', e); }
    try { wireViewedPage(); } catch (e) { log('viewedpage boot fail', e); }
    log('booted in pageType=', ctx.pageType);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else if ('requestIdleCallback' in window) {
    requestIdleCallback(boot, { timeout: 1500 });
  } else {
    setTimeout(boot, 300);
  }
})();
